import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Dropdown,
  Input,
  GridColumn,
  GridRow,
  Grid,
  Button,
  useBreakpoint,
} from '@axiom/ui';
import type { Taxonomy } from '@axiom/validation';

import type { MyBenchSortFiltersForm } from './my-bench-sort-filters-form-schema';
import { MyBenchSortFiltersFormSchema } from './my-bench-sort-filters-form-schema';
import { MyBenchUtil } from './my-bench-util';
import { MyBenchConst } from './my-bench.const';

export type MyBenchSortFiltersProps = {
  name?: string;
  isEnvoyUser: boolean;
  onChange: (prop: MyBenchSortFiltersForm) => void;
  taxonomy: Taxonomy;
};

type FilterKeysType =
  (typeof MyBenchConst.Filters)[keyof typeof MyBenchConst.Filters];

type ShallowNonNullable<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

export const MyBenchSortFilters = ({
  isEnvoyUser,
  onChange,
  taxonomy,
}: MyBenchSortFiltersProps) => {
  const [showFilters, setShowFilters] = useState(true);
  const { isMobile } = useBreakpoint();
  /**
   * "resetting" initValues on change allows the user to reselect the filter's
   * original default values and the change is triggered via the form
   * */
  const [initValues, setInitValues] = useState<MyBenchSortFiltersForm>({
    practiceArea: 'all',
    status: 'all',
    search: null,
    sort: 'default',
  });
  const filterOptions = useMemo(
    () =>
      MyBenchUtil.getFilterOptions({
        taxonomy,
        isEnvoyUser,
      }),
    [isEnvoyUser, taxonomy]
  );

  useEffect(() => {
    // setting filter visibility when sudden screen size change
    if (showFilters && isMobile) {
      setShowFilters(false);
    } else if (!showFilters && !isMobile) {
      setShowFilters(true);
    }
  }, [isMobile]);

  return (
    <Form
      name="BENCH_CANDIDATES_SORT_FILTER_FORM"
      schema={MyBenchSortFiltersFormSchema}
      initialValues={initValues}
      submitOnChange
      onSubmit={(changeData, { setFieldValue }, values) => {
        const rawData: MyBenchSortFiltersForm = { ...values, ...changeData };
        const data: ShallowNonNullable<MyBenchSortFiltersForm> = {
          [MyBenchConst.Filters.PRACTICEAREA]: 'all',
          [MyBenchConst.Filters.SEARCH]: '',
          [MyBenchConst.Filters.SORT]: 'default',
          [MyBenchConst.Filters.STATUS]: 'all',
        };

        for (const [k, value] of Object.entries(rawData)) {
          const key = k as FilterKeysType;

          // Apply default values for null fields
          if (key === MyBenchConst.Filters.SORT) {
            data[key] = (value ?? 'default') as NonNullable<
              MyBenchSortFiltersForm['sort']
            >;
          } else if (key === MyBenchConst.Filters.SEARCH) {
            data[key] = value ?? '';
          } else {
            data[key] = value ?? 'all';
          }

          void setFieldValue(key, data[key]);
        }

        setInitValues(data);
        onChange(data);
      }}
    >
      {() => {
        return (
          <Grid stackable reversed="smallScreen" name="BENCH_FILTERS_SORT">
            <GridRow name="FILERS_SORT_ROW" columns={isMobile ? 12 : 4}>
              {showFilters ? (
                <>
                  <GridColumn>
                    <Dropdown
                      name="practiceArea"
                      label="Filter by Practice Area"
                      options={filterOptions.practiceAreas}
                      displayKey="label"
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn>
                    <Dropdown
                      name="status"
                      label="Filter by Status"
                      options={filterOptions.status}
                      displayKey="label"
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn>
                    <Input
                      name="search"
                      label="Filter by Keyword"
                      placeholder="Search"
                      icon="search"
                    />
                  </GridColumn>
                </>
              ) : null}
              {isMobile ? (
                <GridColumn textAlign="right">
                  <Button
                    icon="filter"
                    name="TOGGLE_FILTERS_BUTTON"
                    onClick={() => {
                      setShowFilters(!showFilters);
                    }}
                    pattern="primary"
                    variation="minimal"
                  >
                    {showFilters ? 'Hide' : 'Show'} filters
                  </Button>
                </GridColumn>
              ) : null}
              <GridColumn>
                <Dropdown
                  name="sort"
                  label="Sort By"
                  options={filterOptions.sort}
                  displayKey="label"
                  valueKey="value"
                />
              </GridColumn>
            </GridRow>
          </Grid>
        );
      }}
    </Form>
  );
};
